import { useCallback, useEffect, useMemo, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import inititializeFirebase from "../../services/firebase";
import { computeIssueDrift } from "./contactDrift";

const functions = getFunctions(inititializeFirebase());
const getStockIssuesCallable = httpsCallable(functions, "getStockIssues");
const updateStockIssueStatusCallable = httpsCallable(functions, "updateStockIssueStatus");
const batchUpdateStockIssueStatusCallable = httpsCallable(
  functions, "batchUpdateStockIssueStatus"
);

const SEVERITY_RANK = { critical: 0, warning: 1, info: 2 };

// Auto-refresh cadence while the tab is visible. 10 minutes is a loose
// floor — the scanner runs daily on cron, so drift trickles in waves, not
// continuously. Tab-focus refresh handles the "came back after lunch"
// case; this interval handles "on a 30-min call with the dashboard open."
const AUTO_REFRESH_INTERVAL_MS = 10 * 60 * 1000;

// Which rows should actually render on the main dashboard. The server
// returns all non-resolved rows; the client hides contacted and dismissed
// rows that haven't drifted since the rep's last decision (those are
// "handled, stable" — they'll come back into view automatically when the
// scanner detects drift or when scope grows).
function shouldRender(issue) {
  if (!issue) return false;
  if (issue.status === "new" || issue.status === "acknowledged") return true;
  if (issue.status === "contacted" || issue.status === "dismissed") {
    return computeIssueDrift(issue).drifted;
  }
  return false;
}

/**
 * Group issues by firebase_project_id and return them sorted by worst
 * severity first, ties broken by oldest detection. `projectLookup` maps
 * firebase_project_id → project metadata (name, distributor, rep) pulled
 * from useProjectSearch; issues for projects the caller doesn't see by
 * name fall back to the raw firebase id as the display name.
 */
function shapeProjects(issues, projectLookup) {
  const byProject = new Map();
  for (const issue of issues) {
    const key = issue.firebase_project_id;
    if (!byProject.has(key)) byProject.set(key, []);
    byProject.get(key).push(issue);
  }

  const groups = Array.from(byProject.entries()).map(([firebaseId, projectIssues]) => {
    const meta = projectLookup?.[firebaseId];
    const worstSeverity = projectIssues.reduce(
      (w, i) => Math.min(w, SEVERITY_RANK[i.severity] ?? 99),
      99
    );
    const oldestDetected = projectIssues.reduce(
      (oldest, i) =>
        !oldest || i.detected_at < oldest ? i.detected_at : oldest,
      null
    );
    // Stable sort within a project so a dismiss→undo puts the row back in
    // the same visual slot it came from, rather than appending to the end.
    // Mirrors the server's list ordering (severity asc, detected_at desc).
    const sortedIssues = [...projectIssues].sort((a, b) => {
      const sa = SEVERITY_RANK[a.severity] ?? 99;
      const sb = SEVERITY_RANK[b.severity] ?? 99;
      if (sa !== sb) return sa - sb;
      return (b.detected_at || "").localeCompare(a.detected_at || "");
    });
    return {
      firebaseId,
      name: meta?.name || firebaseId,
      distributor: meta?.distributor || null,
      accountNumber: meta?.accountNumber || null,
      rep: meta?.rep || null,
      logoURL: meta?.logoURL || null,
      issues: sortedIssues,
      worstSeverity,
      oldestDetected
    };
  });

  groups.sort((a, b) => {
    if (a.worstSeverity !== b.worstSeverity) return a.worstSeverity - b.worstSeverity;
    return (a.oldestDetected || "").localeCompare(b.oldestDetected || "");
  });

  return groups;
}

export function useStockIssues(initialFilters = {}, projectLookup = null) {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(initialFilters);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await getStockIssuesCallable(filters);
      // Filter contacted rows to drifted-only at ingest time so shape +
      // severity counts + project groupings all operate on the visible set.
      setIssues((data.issues || []).filter(shouldRender));
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh: (1) when the tab BECOMES visible (returning from
  // another browser tab) — via visibilitychange, which fires only on
  // document show/hide, not on in-page focus shuffles like Ctrl+F or a
  // modal opening; (2) on a slow interval while visible, so drift
  // surfaces for a rep who keeps the dashboard open all day. We do NOT
  // listen on `window.focus` — it fires when the browser's find bar or
  // any OS-level UI returns focus to the window, which feels like an
  // aggressive mid-scroll refresh.
  useEffect(() => {
    const refreshIfVisible = () => {
      if (!document.hidden) load();
    };
    document.addEventListener("visibilitychange", refreshIfVisible);
    const intervalId = setInterval(refreshIfVisible, AUTO_REFRESH_INTERVAL_MS);
    return () => {
      document.removeEventListener("visibilitychange", refreshIfVisible);
      clearInterval(intervalId);
    };
  }, [load]);

  // Sync the filters state to the caller's argument — lets the parent flip
  // severity / includeResolved without re-mounting the hook. JSON.stringify
  // in the dep is a pragmatic deep-equality check so callers don't have to
  // memoize the options object they pass in.
  useEffect(() => { setFilters(initialFilters); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(initialFilters)]
  );

  const reconcileUpdated = useCallback((updatedRow) => {
    setIssues((prev) => {
      // A mutated row leaves the visible set unless it still meets the
      // "should render" rule: new/acknowledged always; contacted only when
      // drifted. Fresh mark-contacted rows have snapshot == live (no drift)
      // and drop out; drifted-contacted rows stay.
      if (!shouldRender(updatedRow)) {
        return prev.filter((i) => i.id !== updatedRow.id);
      }
      // Undo / undismiss path: the row may no longer be in state (it was
      // removed when dismissed). If so, add it. Otherwise replace in-place.
      const exists = prev.some((i) => i.id === updatedRow.id);
      if (!exists) return [...prev, updatedRow];
      return prev.map((i) => (i.id === updatedRow.id ? updatedRow : i));
    });
  }, []);

  // Omit `notes` entirely when the caller doesn't supply one. Firebase's
  // callable serializer turns `undefined` into `null` on the wire, and the
  // server-side validator rejects non-string notes (so `null` trips it).
  const updateStatus = useCallback(async (id, status, notes) => {
    const payload = { id, status };
    if (typeof notes === "string") payload.notes = notes;
    const { data } = await updateStockIssueStatusCallable(payload);
    reconcileUpdated(data.issue);
    return data.issue;
  }, [reconcileUpdated]);

  const batchUpdate = useCallback(async (issueIds, status, notes) => {
    const payload = { issueIds, status };
    if (typeof notes === "string") payload.notes = notes;
    const { data } = await batchUpdateStockIssueStatusCallable(payload);
    for (const row of data.updated) reconcileUpdated(row);
    return data; // { updated, skipped }
  }, [reconcileUpdated]);

  const projects = useMemo(
    () => shapeProjects(issues, projectLookup),
    [issues, projectLookup]
  );

  // The scanner writes `last_seen_at = now()` on every rescan of an active
  // row (see scan_stock_issues step B). So max(last_seen_at) across the
  // returned rows = the last time the parser looked at this data. This is
  // what reps care about — "is the OOS pipeline still running?" — which is
  // distinct from "did I refetch client data recently."
  const lastScanAt = useMemo(() => {
    let latest = null;
    for (const i of issues) {
      if (!i.last_seen_at) continue;
      if (!latest || i.last_seen_at > latest) latest = i.last_seen_at;
    }
    return latest;
  }, [issues]);

  return {
    issues,
    projects,
    lastScanAt,
    loading,
    error,
    filters,
    setFilters,
    reload: load,
    reconcileUpdated,
    updateStatus,
    batchUpdate
  };
}
